import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import './ShiftTimePicker.css';

const DURATION_HOURS = Array.from({ length: 9 }, (_, i) => i); // 0-8 hours
const DURATION_MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

class DurationPicker extends Component {
    constructor(props) {
        super(props);
        const mins = props.value || 30;
        this.state = {
            open: false,
            isMobile: typeof window !== 'undefined' && window.innerWidth < 768,
            pickedHour: Math.floor(mins / 60),
            pickedMinute: Math.round((mins % 60) / 5) * 5 % 60,
        };
        this.hourRef = React.createRef();
        this.minuteRef = React.createRef();
        this.inputRef = React.createRef();
    }

    componentDidMount() {
        this._resizeHandler = () => {
            this.setState({ isMobile: window.innerWidth < 768 });
        };
        window.addEventListener('resize', this._resizeHandler);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this._resizeHandler);
    }

    componentDidUpdate(prevProps, prevState) {
        if (!prevState.open && this.state.open) {
            requestAnimationFrame(() => this.scrollToSelected());
        }
    }

    scrollToSelected = () => {
        [this.hourRef, this.minuteRef].forEach(ref => {
            if (!ref.current) return;
            const sel = ref.current.querySelector('.stp-slot--selected');
            if (sel) {
                sel.scrollIntoView({ block: 'center' });
            }
        });
    }

    getLabel() {
        const mins = this.props.value || 0;
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        if (h === 0) return `${m} min`;
        if (m === 0) return `${h}h`;
        return `${h}h ${m}m`;
    }

    open = () => {
        const mins = this.props.value || 30;
        this.setState({
            open: true,
            pickedHour: Math.floor(mins / 60),
            pickedMinute: Math.round((mins % 60) / 5) * 5 % 60,
        });
    }

    close = () => {
        const totalMins = this.state.pickedHour * 60 + this.state.pickedMinute;
        this.setState({ open: false });
        if (this.props.onChange) this.props.onChange(Math.max(5, totalMins));
    }

    getDesktopPosition() {
        if (!this.inputRef.current) return {};
        const rect = this.inputRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const dropUp = spaceBelow < 320;
        const dropdownWidth = Math.max(rect.width, 220);
        const pad = 12;
        // Clamp left so dropdown doesn't overflow screen edges
        let left = rect.left;
        if (left + dropdownWidth > window.innerWidth - pad) {
            left = window.innerWidth - dropdownWidth - pad;
        }
        if (left < pad) left = pad;
        return {
            position: 'fixed',
            left,
            width: dropdownWidth,
            ...(dropUp
                ? { bottom: window.innerHeight - rect.top + 4 }
                : { top: rect.bottom + 4 }
            ),
        };
    }

    renderColumns() {
        const { pickedHour, pickedMinute } = this.state;
        return (
            <div className="stp-columns">
                <div className="stp-column" ref={this.hourRef}>
                    <div className="stp-column-label">Hours</div>
                    <div className="stp-column-scroll">
                        {DURATION_HOURS.map(h => (
                            <button
                                key={h}
                                type="button"
                                className={'stp-slot' + (h === pickedHour ? ' stp-slot--selected' : '')}
                                onClick={() => this.setState({ pickedHour: h })}
                            >
                                {h}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="stp-column-divider" />
                <div className="stp-column" ref={this.minuteRef}>
                    <div className="stp-column-label">Min</div>
                    <div className="stp-column-scroll">
                        {DURATION_MINUTES.map(m => (
                            <button
                                key={m}
                                type="button"
                                className={'stp-slot' + (m === pickedMinute ? ' stp-slot--selected' : '')}
                                onClick={() => this.setState({ pickedMinute: m })}
                            >
                                {String(m).padStart(2, '0')}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    renderDesktop() {
        if (!this.state.open) return null;
        return ReactDOM.createPortal(
            <React.Fragment>
                <div className="stp-desktop-backdrop" onClick={this.close} />
                <div className="stp-desktop-dropdown" style={this.getDesktopPosition()}>
                    {this.renderColumns()}
                </div>
            </React.Fragment>,
            document.body
        );
    }

    renderMobile() {
        if (!this.state.open) return null;
        return ReactDOM.createPortal(
            <div className="stp-mobile-overlay" onClick={this.close}>
                <div className="stp-mobile-sheet" onClick={e => e.stopPropagation()}>
                    <div className="stp-sheet-header">
                        <span className="stp-sheet-title">{this.props.label || 'Duration'}</span>
                        <button type="button" className="stp-sheet-close" onClick={this.close}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                            </svg>
                        </button>
                    </div>
                    <div className="stp-sheet-divider" />
                    {this.renderColumns()}
                    <div className="stp-confirm-bar">
                        <button type="button" className="stp-confirm-btn" onClick={this.close}>
                            Confirm
                        </button>
                    </div>
                </div>
            </div>,
            document.body
        );
    }

    render() {
        const label = this.getLabel();
        return (
            <React.Fragment>
                <div
                    ref={this.inputRef}
                    className="stp-input"
                    onClick={this.open}
                >
                    <span className="stp-input-value">{label}</span>
                </div>
                {this.state.isMobile ? this.renderMobile() : this.renderDesktop()}
            </React.Fragment>
        );
    }
}

export default DurationPicker;
